import { IFormValuesProfile } from '../../components/modals/editProfile/types';
import { ICreatePassword, ILoginUser, IResetPassword, IVerifyCode } from '../../models/service/service';
import { IFormValues } from '../../pages/createOpportunity/types';
import { OrganizerService } from '../../services';
import { routesNames } from '../../utils/constants';
import { clearLocalStorage, fillLocalStorage } from '../../utils/token';
import { organizerSlice, setResetPasswordEmail } from '../slice/slice';
import { AppDispatch } from '../store';

export const searchPlaces = (query: string):
(dispatch: AppDispatch) => Promise<void> => async(dispatch: AppDispatch) => {
  try {
    const response = await OrganizerService.$mapsAutocomplete(query);
    dispatch(organizerSlice.actions.setAddresses(response.data));
  }
  catch (error) {
    console.log('error', error);
  }
};

export const loginUser = (data: ILoginUser, navigate: (is: string) => void):
(dispatch: AppDispatch) => Promise<void> => {
  const value = {
    email: data.email,
    password: data.password,
  };
  return async(dispatch: AppDispatch) => {
    try {
      const response = await OrganizerService.$loginUser(value);
      if (response.data) {
        const res = response.data;
        fillLocalStorage(res);
      }
    }
    catch (error: any) {
      const statusCode = Object.entries(error)[1][1];
      const refreshToken = localStorage.getItem('admin_refresh_token');
      if (statusCode === 401 && refreshToken) {
        refresh(navigate)(dispatch);
      }
      if (!refreshToken) {
        navigate(routesNames.logIn);
      }
      throw error;
    }
  };
};

export const logoutUser = ():
(dispatch: AppDispatch) => Promise<void> => async(dispatch: AppDispatch) => {
  try {
    await OrganizerService.$logoutUser();
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
  finally {
    clearLocalStorage();
  }
};

export const createPassword = (data: ICreatePassword):
(dispatch: AppDispatch) => Promise<void> => async(dispatch: AppDispatch) => {
  try {
    const response = await OrganizerService.$createPassword(data);
    if (response.data.status !== false) {
      const res = response.data;
      fillLocalStorage(res);
    }
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
};

export const resetPassword = (data: IResetPassword):
(dispatch: AppDispatch) => Promise<void> => async(dispatch: AppDispatch) => {
  try {
    const response = await OrganizerService.$resetPassword(data);
    if (response.data.status !== false) {
      const res = response.data;
      dispatch(setResetPasswordEmail(data.email as string));
    }
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
};

export const verifyCode = (data: IVerifyCode):
(dispatch: AppDispatch) => Promise<void> => async(dispatch: AppDispatch) => {
  try {
    const response = await OrganizerService.$verifyCode(data);
    if (response.data) {
      const res = response.data;
      dispatch(organizerSlice.actions.setInviteToken(res));
    }
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
};

export const refresh = (navigate: (is: string) => void):
(dispatch: AppDispatch) => Promise<void> => async(dispatch: AppDispatch) => {
  try {
    const response = await OrganizerService.$refresh();
    if (response.data) {
      const res = response.data;
      fillLocalStorage(res);
    }
    else {
      clearLocalStorage();
      navigate(routesNames.logIn);
    }
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    navigate(routesNames.logIn);
    throw new Error(message);
  }
};

export const getOpportunities = ():
(dispatch: AppDispatch) => Promise<void> => async(dispatch: AppDispatch) => {
  try {
    dispatch(organizerSlice.actions.loadStarted());
    const response = await OrganizerService.$getOpportunities();
    if (response.data) {
      const res = response.data.opportunities;
      dispatch(organizerSlice.actions.setOpportunities(res));
      dispatch(organizerSlice.actions.loadCompleted());
    }
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
};

export const getOpportunitiesById = (id: string | null):
(dispatch: AppDispatch) => Promise<void> => async(dispatch: AppDispatch) => {
  try {
    dispatch(organizerSlice.actions.loadStarted());
    const response = await OrganizerService.$getOpportunitiesById(id);
    if (response.data) {
      const res = response.data;
      dispatch(organizerSlice.actions.setOpportunitiesById(res.opportunity));
      dispatch(organizerSlice.actions.setVolunteerAmount(res.volunteerAmount));
      dispatch(organizerSlice.actions.loadCompleted());
    }
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
};

export const getRequiredQualification = ():
(dispatch: AppDispatch) => Promise<void> => async(dispatch: AppDispatch) => {
  try {
    const response = await OrganizerService.$getRequiredQualification();
    if (response.data) {
      const res = response.data.qualifications;
      dispatch(organizerSlice.actions.setRequiresQualification(res));
    }
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
};

export const editOpportunities = (data: IFormValues, id: string | null):
(dispatch: AppDispatch) => Promise<void | string> => async(dispatch: AppDispatch) => {
  try {
    await OrganizerService.$editOpportunities(data, id);
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
};

export const getProfile = ():
(dispatch: AppDispatch) => Promise<void> => async(dispatch: AppDispatch) => {
  try {
    dispatch(organizerSlice.actions.loadStarted());
    const response = await OrganizerService.$getProfile();
    if (response.data) {
      const res = response.data;
      dispatch(organizerSlice.actions.setProfile(res));
      dispatch(organizerSlice.actions.loadCompleted());
    }
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
};

export const editProfile = (data: IFormValuesProfile):
(dispatch: AppDispatch) => Promise<void | string> => async(dispatch: AppDispatch) => {
  try {
    await OrganizerService.$editProfile(data);
    getProfile()(dispatch);
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
};

export const deleteOpportunities = (id: string | null):
(dispatch: AppDispatch) => Promise<void | string> => async(dispatch: AppDispatch) => {
  try {
    await OrganizerService.$deleteOpportunities(id);
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
};

export const deleteVolunteer = (opportunityId: string | null, volunteerId: string):
(dispatch: AppDispatch) => Promise<void | string> => async(dispatch: AppDispatch) => {
  try {
    await OrganizerService.$rejectVolunteer(opportunityId, volunteerId);
    dispatch(organizerSlice.actions.deleteVolunteer(volunteerId));
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
};

export const createOpportunities = (data: IFormValues):
(dispatch: AppDispatch) => Promise<void | string> => async(dispatch: AppDispatch) => {
  try {
    await OrganizerService.$createOpportunities(data);
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
};

export const getVolunteers = (id: string | null, skip?: number, limit?: number):
(dispatch: AppDispatch) => Promise<void> => async(dispatch: AppDispatch) => {
  try {
    dispatch(organizerSlice.actions.loadStarted());
    const response = await OrganizerService.$getVolunteers(id, skip, limit);
    if (response.data) {
      const res = response.data;
      dispatch(organizerSlice.actions.setVolunteers(res));
      dispatch(organizerSlice.actions.loadCompleted());
    }
  }
  catch (error) {
    let message: string = '';
    if (error instanceof Error) {
      message = error.message;
    }
    else {
      message = String(error);
    }
    throw new Error(message);
  }
};
